import { type DatabaseAgentContract } from '@databases/ai-first-creation-database-agent-types';
import {
  CsvErrorCategory,
  type CsvErrorCollector,
  CsvErrorType,
  type ValueToken
} from '@databases/streaming-csv-parser';
import { type UserReference } from '@databases/types/confluence';
import {
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseTextValue,
  type DatabaseValue,
  NumberFormats
} from '@databases/types/database';
import { getInitialValue } from '@databases/utils/database/field-types';
import { parseTextBasedFromHtml } from '@databases/utils/database/field-types/text';
import { parseHTML } from '@databases/utils/lib/dom';
import { matchViewPageOrBlogpostPath } from '@databases/utils/urls';
import { generateId } from '@databases/utils/utils';

import type { ExternalDataResolver } from '../resolvers/external-data-resolver.ts';
import type {
  AiFieldStreamTarget,
  AiFieldGenerationWriteContext,
  DatabaseValueChange,
  ProcessingError,
  TextValueStrategy
} from '../types.ts';
import {
  extractIssueKeyFromUrl,
  logValidationErrors,
  splitMultiValue
} from '../utils.ts';

import type { TokenProcessingLifecycle } from './token-processing-lifecycle.ts';

const PARSED_EXTRA = '__parsed_extra';
export class ValueProcessor {
  private agent: DatabaseAgentContract;
  private currentEntryId: string | undefined;
  private currentFieldId: string | undefined;
  private lifecycle: TokenProcessingLifecycle;
  private errorCollector?: CsvErrorCollector;
  private hasLoggedParsedExtraError = false;
  private readonly resolver: ExternalDataResolver;
  private aiFieldGenerationWriteContext: AiFieldGenerationWriteContext | undefined;

  constructor(
    agent: DatabaseAgentContract,
    lifecycle: TokenProcessingLifecycle,
    resolver: ExternalDataResolver,
    errorCollector?: CsvErrorCollector,
    private readonly aiFieldTarget?: AiFieldStreamTarget,
    private readonly textValueStrategy: TextValueStrategy = { kind: 'plain-text' }
  ) {
    this.agent = agent;
    this.lifecycle = lifecycle;
    this.errorCollector = errorCollector;
    this.resolver = resolver;
  }

  public setAiFieldGenerationWriteContext(context: AiFieldGenerationWriteContext): void {
    this.aiFieldGenerationWriteContext = context;
  }

  private addError(error: ProcessingError): void {
    this.errorCollector?.addError(
      error.message,
      CsvErrorCategory.VALUE,
      CsvErrorType.VALIDATION,
      undefined,
      error.severity
    );
  }

  private addErrors(errors: ProcessingError[]): void {
    for (const error of errors) {
      this.addError(error);
    }
  }

  public setCurrentEntryId(entryId: string): void {
    this.clearCurrentFieldId();
    this.currentEntryId = entryId;
    this.lifecycle.setEntryIdForProcessing(entryId);
  }

  public setCurrentFieldId(fieldId: string): void {
    this.currentFieldId = fieldId;
    this.lifecycle.setFieldIdForProcessing(fieldId);
  }

  public clearCurrentEntryId(): void {
    this.currentEntryId = undefined;
    this.lifecycle.clearEntryIdForProcessing();
  }

  public clearCurrentFieldId(): void {
    this.currentFieldId = undefined;
    this.lifecycle.clearFieldIdForProcessing();
  }

  public getCurrentEntryId(): string | undefined {
    return this.currentEntryId;
  }

  public getCurrentFieldId(): string | undefined {
    return this.currentFieldId;
  }

  private static isDatabaseValueChange(
    result: DatabaseValue | DatabaseValueChange
  ): result is DatabaseValueChange {
    return 'databaseValue' in result && 'entryIdToChange' in result;
  }

  public processValueToken(valueToken: ValueToken): void {
    if (!this.currentEntryId) {
      const errors = [
        {
          message: 'No current entry ID set. Cannot process value token.'
        }
      ];
      logValidationErrors(errors);
      this.addErrors(errors);
      return;
    }

    const validationResult = this.validateValue(valueToken);

    if (validationResult.errors.length === 0) {
      const { fieldId, field } = validationResult;

      // these values should always exist if validation passes. This is for typescript.
      if (!fieldId || !field) {
        return;
      }

      // Change-aware AI field runs write blank results to persist their generation fingerprint.
      if (
        (valueToken.value === null || valueToken.value === '') &&
        this.aiFieldGenerationWriteContext === undefined
      ) {
        return;
      }

      if (this.aiFieldTarget) {
        this.processAiFieldValue(valueToken, fieldId, field);
        return;
      }

      if (valueToken.value === 'NULL') {
        this.agent.clearValue(this.currentEntryId, fieldId);
        this.lifecycle.markValueProcessed(fieldId, this.currentEntryId);
        return;
      }

      this.setCurrentFieldId(fieldId);
      const capturedEntryId = this.currentEntryId;
      this.processValueByFieldType(field, fieldId, valueToken.value)
        .then((result) => {
          let entryId = capturedEntryId;
          if (ValueProcessor.isDatabaseValueChange(result)) {
            entryId = result.entryIdToChange;
            this.agent.setValue(
              entryId,
              fieldId,
              result.databaseValue,
              valueToken.resetValue
            );
          } else if (capturedEntryId) {
            this.agent.setValue(capturedEntryId, fieldId, result, valueToken.resetValue);
          }
          if (entryId) {
            this.lifecycle.markValueProcessed(fieldId, entryId);
          }
        })
        .catch((error) => {
          const errors = [
            {
              message: `Failed to set value for field ${valueToken.fieldName}: ${error}`
            }
          ];
          logValidationErrors(errors);
          this.addErrors(errors);
        });
    } else {
      logValidationErrors(validationResult.errors);
      this.addErrors(validationResult.errors);
    }
  }

  private processAiFieldValue(
    valueToken: ValueToken,
    fieldId: string,
    field: DatabaseField
  ): void {
    if (this.isEntryOutsideAiFieldTarget(this.currentEntryId)) {
      this.addError({
        message: `AI field stream cannot write to entry ${String(this.currentEntryId)}`
      });
      return;
    }

    if (fieldId !== this.aiFieldTarget?.fieldId) {
      this.addError({
        message: `AI field stream cannot write to field ${valueToken.fieldName}`
      });
      return;
    }

    switch (this.aiFieldTarget.aiFieldType) {
      case 'summary':
        if (field.type !== DatabaseFieldType.AI_SUMMARY) {
          this.addError({
            message: `Target field ${valueToken.fieldName} is not an AI Summary field`
          });
          return;
        }
        if (!this.agent.writeAiSummaryCell || !this.currentEntryId) {
          this.addError({ message: 'AI Summary cell writer is unavailable' });
          return;
        }
        try {
          this.setCurrentFieldId(fieldId);
          const value = this.processTextValue(valueToken.value);
          if (value.plain.trim() === '') {
            return;
          }
          this.aiFieldGenerationWriteContext?.onGeneratedValueReceived?.(
            this.currentEntryId
          );
          if (
            this.aiFieldGenerationWriteContext !== undefined &&
            !this.aiFieldGenerationWriteContext.shouldCommit(this.currentEntryId)
          ) {
            this.lifecycle.markValueProcessed(fieldId, this.currentEntryId);
            return;
          }
          const generation = this.aiFieldGenerationWriteContext?.getFingerprint(
            this.currentEntryId
          );
          this.agent.writeAiSummaryCell({
            entryId: this.currentEntryId,
            fieldId,
            value,
            actor: 'ai',
            ...(generation !== undefined && { generation })
          });
          this.lifecycle.markValueProcessed(fieldId, this.currentEntryId);
          this.aiFieldGenerationWriteContext?.onGeneratedValueCommitted?.(
            this.currentEntryId
          );
        } catch (error) {
          this.addError({
            message: `Failed to write AI Summary value for field ${valueToken.fieldName}: ${error}`
          });
          return;
        }
        break;
      default: {
        const unsupportedAiFieldType: never = this.aiFieldTarget.aiFieldType;
        this.addError({
          message: `Unsupported AI field type: ${String(unsupportedAiFieldType)}`
        });
      }
    }
  }

  private isEntryOutsideAiFieldTarget(entryId: string | undefined): boolean {
    if (!this.aiFieldTarget) {
      return false;
    }

    switch (this.aiFieldTarget.scope) {
      case 'field':
        return false;
      case 'entry':
        return entryId !== this.aiFieldTarget.entryId;
      case 'entries':
        return entryId === undefined || !this.aiFieldTarget.entryIds.includes(entryId);
      default: {
        const unhandledTarget: never = this.aiFieldTarget;
        return unhandledTarget;
      }
    }
  }

  private processTextValue(value: string): DatabaseTextValue {
    switch (this.textValueStrategy.kind) {
      case 'rich-text-html': {
        const html = parseHTML(value);
        if (html.children().length === 0) {
          return { plain: value };
        }

        return parseTextBasedFromHtml(html);
      }
      case 'plain-text':
        return { plain: value };
      default: {
        const unsupportedStrategy: never = this.textValueStrategy;
        throw new Error(
          `Unsupported text value strategy: ${String(unsupportedStrategy)}`
        );
      }
    }
  }

  private validateValue(valueToken: ValueToken): {
    errors: ProcessingError[];
    fieldId?: string;
    field?: DatabaseField;
  } {
    const errors: ProcessingError[] = [];

    if (!valueToken.fieldName) {
      errors.push({
        message: 'Field name is missing'
      });
      return { errors };
    }

    if (valueToken.value === undefined) {
      errors.push({
        message: `Value is missing for field ${valueToken.fieldName}`
      });
    }

    if (!this.currentEntryId) {
      errors.push({
        message: 'No current entry to set value for'
      });
    }

    const fieldId = this.agent.getFieldIdByName(valueToken.fieldName);
    if (!fieldId) {
      const isParsedExtraField = valueToken.fieldName.startsWith(PARSED_EXTRA);
      if (isParsedExtraField) {
        if (!this.hasLoggedParsedExtraError) {
          this.hasLoggedParsedExtraError = true;
          errors.push({
            message:
              'Some rows contain more values than fields. Extra values will be dropped.'
          });
        }
        return { errors };
      }
      errors.push({
        message: `Field ${valueToken.fieldName} not found`
      });
      return { errors };
    }

    const field = this.agent.getFieldById(fieldId);
    if (!field) {
      errors.push({
        message: `Field with ID ${fieldId} not found`
      });
      return { errors };
    }

    return { errors, fieldId, field };
  }

  public async processValueByFieldType(
    field: DatabaseField,
    fieldId: string,
    value: string
  ): Promise<DatabaseValue | DatabaseValueChange> {
    return await this.convertValueByFieldType(this.agent, field, fieldId, value);
  }

  public async convertValueByFieldType(
    agent: DatabaseAgentContract,
    field: DatabaseField,
    fieldId: string,
    value: string,
    isFilter?: boolean
  ): Promise<DatabaseValue | DatabaseValueChange> {
    switch (field.type) {
      case DatabaseFieldType.TEXT:
        return {
          type: DatabaseFieldType.TEXT,
          value: this.processTextValue(value)
        };

      case DatabaseFieldType.NUMBER:
        return ValueProcessor.processNumberValue(value, field);

      case DatabaseFieldType.DATE:
        return ValueProcessor.processDateValue(value);

      case DatabaseFieldType.SELECT:
        return ValueProcessor.processSelectValue(agent, fieldId, value);

      case DatabaseFieldType.HYPERLINK:
        return ValueProcessor.processHyperlinkValue(value);

      case DatabaseFieldType.USER:
        if (isFilter) {
          return await this.processUserValueForFilter(splitMultiValue(value));
        }

        if (this.currentEntryId) {
          return await this.processUserValue(splitMultiValue(value), this.currentEntryId);
        }
        return getInitialValue(field.type);

      case DatabaseFieldType.JIRA_ISSUE:
        if (this.currentEntryId) {
          return await this.processJiraIssueValue(
            fieldId,
            splitMultiValue(value),
            this.currentEntryId
          );
        }
        return getInitialValue(field.type);

      case DatabaseFieldType.PAGE_LINK:
        if (this.currentEntryId) {
          return this.processPageLinkValue(value, this.currentEntryId);
        }
        return getInitialValue(field.type);

      case DatabaseFieldType.ATTACHMENT:
      case DatabaseFieldType.CHECKBOX:
      case DatabaseFieldType.PAGE_LABELS:
      case DatabaseFieldType.PAGE_STATUS:
      case DatabaseFieldType.PAGE_DETAILS:
      case DatabaseFieldType.JIRA_ISSUE_DETAILS:
      case DatabaseFieldType.ENTRY_LINK:
      case DatabaseFieldType.ENTRY_DETAILS:
      case DatabaseFieldType.BACKLINK:
        return getInitialValue(field.type);

      default:
        throw new Error(`Unsupported field type for value processing: ${field.type}`);
    }
  }

  private static processNumberValue(value: string, field: DatabaseField): DatabaseValue {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return {
        type: DatabaseFieldType.NUMBER,
        value: String(value)
      };
    }

    let formattedValue = String(value);
    // If we are processing a percent field and the value is greater than 1, divide by 100 to get the decimal value
    if (field.settings?.numberFormat?.format === NumberFormats.PERCENT && numValue > 1) {
      formattedValue = String(numValue / 100);
    }

    return {
      type: DatabaseFieldType.NUMBER,
      value: formattedValue
    };
  }

  private static processDateValue(value: string): DatabaseValue {
    return {
      type: DatabaseFieldType.DATE,
      value: value
    };
  }

  private static processSelectValue(
    agent: DatabaseAgentContract,
    fieldId: string,
    value: string
  ): DatabaseValue {
    const values = splitMultiValue(value);

    const optionIdsOrValues = [];
    for (const value of values) {
      const optionId = ValueProcessor.getOrCreateSelectOption(agent, fieldId, value);
      optionIdsOrValues.push(optionId ? optionId : value);
    }

    return {
      type: DatabaseFieldType.SELECT,
      value: optionIdsOrValues
    };
  }

  private static processHyperlinkValue(value: string): DatabaseValue {
    const urls = splitMultiValue(value);

    return {
      type: DatabaseFieldType.HYPERLINK,
      value: urls.map((url) => ({
        url,
        _id: generateId()
      }))
    };
  }

  private async processUserValueForFilter(accountIds: string[]): Promise<DatabaseValue> {
    const results = await this.resolver.resolveUsers(accountIds);

    const errors: ProcessingError[] = [];
    const users: UserReference[] = [];

    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      if (result !== null) {
        users.push(result);
      } else {
        errors.push({ message: `User not found: ${accountIds[i]}` });
      }
    }

    if (errors.length > 0) {
      logValidationErrors(errors);
      this.addErrors(errors);
    }

    return {
      type: DatabaseFieldType.USER,
      value: users.map((user) => ({
        accountId: user.accountId,
        _id: generateId()
      }))
    };
  }

  private async processUserValue(
    accountIds: string[],
    entryId: string
  ): Promise<DatabaseValueChange> {
    const databaseValue = await this.processUserValueForFilter(accountIds);
    return {
      entryIdToChange: entryId,
      databaseValue
    };
  }

  private async processPageLinkValue(
    value: string,
    entryId: string
  ): Promise<DatabaseValueChange> {
    const urls = splitMultiValue(value);
    const validationResults = await Promise.all(
      urls.map((url) => this.validatePageLink(url))
    );

    const pageLinks: { contentId?: string; _id: string }[] = [];

    for (const validationResult of validationResults) {
      if (validationResult.errors.length > 0 || !validationResult.contentId) {
        logValidationErrors(validationResult.errors);
        this.addErrors(validationResult.errors);
      }

      if (validationResult.contentId) {
        pageLinks.push({
          contentId: validationResult.contentId,
          _id: generateId()
        });
      }
    }

    return {
      entryIdToChange: entryId,
      databaseValue: {
        type: DatabaseFieldType.PAGE_LINK,
        value: pageLinks
      }
    };
  }

  private async validatePageLink(value: string): Promise<{
    errors: ProcessingError[];
    contentId?: string;
  }> {
    const errors: ProcessingError[] = [];
    const contentId = matchViewPageOrBlogpostPath(value)?.contentId;

    if (!contentId) {
      errors.push({
        message: `Content ID not found for page link value: ${value}`
      });
      return { errors };
    }

    const result = await this.resolver.resolvePageLink(contentId);
    if (!result) {
      errors.push({
        message: `Content not found for content ID: ${contentId}`
      });
      return { errors };
    }

    return { errors, contentId: result.contentId };
  }

  private async processJiraIssueValue(
    fieldId: string,
    urls: string[],
    entryId: string
  ): Promise<DatabaseValueChange> {
    const errors: ProcessingError[] = [];

    // Retrieve cloudId and set it on field if successful.
    // Try each URL in sequence until one resolves a cloudId
    let cloudId = this.agent.getCloudId(fieldId);
    const issueKeys = urls.map((url) => extractIssueKeyFromUrl(url));
    if (!cloudId) {
      for (const url of urls) {
        const newCloudId = await this.resolver.resolveCloudId(url);
        if (newCloudId) {
          cloudId = newCloudId;
          this.agent.setCloudId(fieldId, cloudId);
          break;
        }
      }
    }

    if (!cloudId) {
      const jiraErrors = [{ message: "Couldn't retrieve Jira instance" }];
      logValidationErrors(jiraErrors);
      this.addErrors(jiraErrors);
      return {
        entryIdToChange: entryId,
        databaseValue: {
          type: DatabaseFieldType.JIRA_ISSUE,
          value: []
        }
      };
    }

    const results = await this.resolver.resolveJiraIssues(issueKeys, cloudId);

    const issueKeyResults: string[] = [];
    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      if (result !== null) {
        issueKeyResults.push(result);
      } else {
        errors.push({ message: `Jira issue not found: ${issueKeys[i]}` });
      }
    }

    if (errors.length > 0) {
      logValidationErrors(errors);
      this.addErrors(errors);
    }

    return {
      entryIdToChange: entryId,
      databaseValue: {
        type: DatabaseFieldType.JIRA_ISSUE,
        value: issueKeyResults.map((key) => ({
          key,
          _id: generateId()
        }))
      }
    };
  }

  private static getOrCreateSelectOption(
    agent: DatabaseAgentContract,
    fieldId: string,
    optionValue: string
  ): string | undefined {
    const field = agent.getFieldById(fieldId);
    if (!field || field.type !== DatabaseFieldType.SELECT) {
      return;
    }

    const existingOption = field.settings.selectOptions?.find(
      (option) => option.id === optionValue || option.label === optionValue
    );

    if (existingOption) {
      return existingOption.id;
    }

    try {
      return agent.getOrCreateSelectOption(fieldId, optionValue);
    } catch (error) {
      throw new Error(
        `Failed to create select option ${optionValue} for field ${fieldId}: ${error}`
      );
    }
  }
}
